import React, { useState } from 'react';
import axios from "@/lib/axios";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";
import { API_BASE_URL } from '@/config';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import MandateFAQModal from '@/components/MandateFAQModal';

/**
 * "Change Bank Details" escape hatch for the eMandate page.
 *
 * Why this exists
 * ---------------
 * The eMandate page is a dead end when a mandate cannot be registered — usually
 * because the account number or IFSC entered on the Bank Details page is wrong.
 * Continue on that page only unlocks once a mandate reaches status=4, so without
 * this the user is stuck and has to be rewound manually by support.
 *
 * The journey position lives on the BACKEND (LoanJourneyState), so a client-side
 * navigation alone would be undone by the next journey-state fetch. This calls
 * POST /api/journey/go-to-bank-details/ which rewinds the caller's own journey to
 * the BANK_ACCOUNT_AUTH page, then reloads so JourneyPage re-resolves the page
 * from backend state.
 *
 * The backend refuses with 409 once a mandate is already registered (bank details
 * are then locked — they are on the mandate at the bank); its message is shown.
 *
 * Rendered by JourneyPage at the TOP of the eMandate page (above both mandate
 * cards) so it is visible before the user starts, not buried under the NACH card
 * which is itself locked until UPI completes or is skipped.
 */
interface ChangeBankDetailsLinkProps {
    /** Disable while the page has its own request in flight (e.g. Continue saving). */
    disabled?: boolean;
    /** Optional helper line above the button. */
    hint?: string;
}

const ChangeBankDetailsLink: React.FC<ChangeBankDetailsLinkProps> = ({
    disabled = false,
    hint = "Mandate not going through? Your bank account number or IFSC may be incorrect.",
}) => {
    const [submitting, setSubmitting] = useState(false);
    // In-app confirmation. Deliberately NOT window.confirm(): that renders the
    // raw browser chrome ("13.205.x.x says…"), which looks like a phishing
    // prompt on a lending journey and cannot be styled. AlertDialog is the
    // pattern already used for every other destructive confirm in this app
    // (see BankAccountAuthentication.tsx, LoanAmountConfirmation.tsx).
    const [confirmOpen, setConfirmOpen] = useState(false);

    const handleConfirm = async () => {
        setConfirmOpen(false);
        setSubmitting(true);

        try {
            const res = await axios.post(`${API_BASE_URL}/api/journey/go-to-bank-details/`);
            const cancelled = res.data?.cancelled_mandates?.length || 0;
            toast.success(
                cancelled > 0
                    ? `Cancelled ${cancelled} mandate(s). Taking you back to your bank details…`
                    : "Taking you back to your bank details…"
            );
            // Full reload — JourneyPage resolves the current page purely from
            // backend state, so this lands the user on the Bank Details page.
            window.location.reload();
        } catch (err: any) {
            setSubmitting(false);
            toast.error(
                err.response?.data?.detail
                || "Could not take you back to bank details. Please try again."
            );
        }
    };

    return (
        <>
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                {hint && <p className="text-xs text-amber-800 mb-2">{hint}</p>}
                <button
                    type="button"
                    onClick={() => setConfirmOpen(true)}
                    disabled={disabled || submitting}
                    className="w-full rounded-lg border border-amber-300 bg-white py-2.5 text-sm font-bold text-amber-900 transition-all active:scale-[0.98] hover:bg-amber-100 disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100"
                >
                    {submitting ? "Taking you back…" : "Change Bank Details"}
                </button>
                <div className="mt-3 flex justify-center">
                    <MandateFAQModal variant="amber" />
                </div>
            </div>

            <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
                <AlertDialogContent className="max-w-sm rounded-2xl">
                    <AlertDialogHeader>
                        <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-amber-100">
                            <AlertTriangle className="h-6 w-6 text-amber-600" />
                        </div>
                        <AlertDialogTitle className="text-center text-lg font-bold">
                            Change your bank details?
                        </AlertDialogTitle>
                        <AlertDialogDescription className="text-center text-sm text-gray-600">
                            You will go back to Bank Account Details to correct your account
                            number or IFSC.
                            {/* Spelled out because this IS destructive — the backend cancels
                                every mandate registered so far at Digio before rewinding,
                                since those mandates point at the account being replaced. */}
                            <span className="mt-3 block rounded-lg bg-amber-50 p-3 text-left text-xs text-amber-900">
                                Any repayment mandate you have already set up will be
                                <strong> cancelled</strong>. You will need to verify your bank
                                account and set up the mandate again.
                            </span>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="flex-col gap-2 sm:flex-row">
                        <AlertDialogCancel className="mt-0 h-11 w-full rounded-xl border-gray-200 font-semibold hover:bg-gray-50">
                            Keep current details
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleConfirm}
                            className="h-11 w-full rounded-xl bg-intellend-green font-bold hover:bg-intellend-green/90"
                        >
                            Yes, change them
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
};

export default ChangeBankDetailsLink;
